import styled from '@emotion/styled';
import { forwardRef, useCallback, useMemo, useState, useEffect, useRef } from 'react';

import type { PaginatorProps, PaginatorSize, PageSizeOption } from './Paginator.types';

// Default page size options
const defaultPageSizeOptions: PageSizeOption[] = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

/**
 * Container for the paginator
 */
const PaginatorContainer = styled.div<{ $disabled: boolean; $inverse: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  color: ${({ $inverse, theme }) => ($inverse ? theme.colors.white : theme.colors.text.primary)};
`;

/**
 * Base button styles for paginator controls
 */
const PaginatorButton = styled.button<{
  $size: PaginatorSize;
  $active?: boolean;
  $inverse?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $inverse, theme }) =>
    $inverse ? 'rgba(255, 255, 255, 0.3)' : theme.colors.border.medium};
  background-color: ${({ $active, $inverse, theme }) => {
    if ($active) return theme.colors.highlight;
    if ($inverse) return 'transparent';
    return theme.colors.background.default;
  }};
  color: ${({ $active, $inverse, theme }) => {
    if ($active) return theme.colors.white;
    if ($inverse) return theme.colors.white;
    return theme.colors.text.primary;
  }};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  border-radius: ${({ theme }) => theme.borders.radius.sm};

  /* Size variants */
  ${({ $size, theme }) => {
    switch ($size) {
      case 'sm':
        return `
          min-width: 28px;
          height: 28px;
          padding: 4px 8px;
          font-size: ${theme.typography.fontSize.xs};
        `;
      case 'lg':
        return `
          min-width: 40px;
          height: 40px;
          padding: 8px 12px;
          font-size: ${theme.typography.fontSize.md};
        `;
      default:
        return `
          min-width: 32px;
          height: 32px;
          padding: 6px 10px;
          font-size: ${theme.typography.fontSize.sm};
        `;
    }
  }}

  &:not(:disabled):hover {
    background-color: ${({ $active, $inverse, theme }) => {
      if ($active) return theme.colors.highlight;
      if ($inverse) return 'rgba(255, 255, 255, 0.1)';
      return theme.colors.background.hover;
    }};
    opacity: ${({ $active }) => ($active ? 0.9 : 1)};
  }

  &:not(:disabled):active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlight};
    outline-offset: 2px;
  }
`;

/**
 * Icon button for navigation (first, prev, next, last)
 */
const NavButton = styled(PaginatorButton)`
  /* Icon styles */
  i,
  .material-icons {
    font-family: ${({ theme }) => theme.typography.fontFamilyIcons};
    font-size: ${({ $size }) => {
      switch ($size) {
        case 'sm':
          return '18px';
        case 'lg':
          return '26px';
        default:
          return '22px';
      }
    }};
  }

  svg {
    width: ${({ $size }) => {
      switch ($size) {
        case 'sm':
          return '16px';
        case 'lg':
          return '24px';
        default:
          return '20px';
      }
    }};
    height: ${({ $size }) => {
      switch ($size) {
        case 'sm':
          return '16px';
        case 'lg':
          return '24px';
        default:
          return '20px';
      }
    }};
  }
`;

/**
 * Page number button
 */
const PageButton = styled(PaginatorButton)`
  /* Page number specific styles */
`;

/**
 * Ellipsis indicator
 */
const Ellipsis = styled.span<{ $size: PaginatorSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  min-width: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return '20px';
      case 'lg':
        return '32px';
      default:
        return '24px';
    }
  }};
  font-size: ${({ $size, theme }) => {
    switch ($size) {
      case 'sm':
        return theme.typography.fontSize.xs;
      case 'lg':
        return theme.typography.fontSize.md;
      default:
        return theme.typography.fontSize.sm;
    }
  }};
`;

/**
 * Page info container (for simple mode)
 */
const PageInfoContainer = styled.div<{ $size: PaginatorSize; $inverse: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ $inverse, theme }) => ($inverse ? theme.colors.white : theme.colors.text.primary)};
  font-size: ${({ $size, theme }) => {
    switch ($size) {
      case 'sm':
        return theme.typography.fontSize.xs;
      case 'lg':
        return theme.typography.fontSize.md;
      default:
        return theme.typography.fontSize.sm;
    }
  }};
`;

/**
 * Go to page input field
 */
const GoToPageInput = styled.input<{ $size: PaginatorSize; $inverse: boolean }>`
  border: 1px solid ${({ $inverse, theme }) =>
    $inverse ? 'rgba(255, 255, 255, 0.3)' : theme.colors.border.medium};
  background-color: ${({ $inverse, theme }) =>
    $inverse ? 'rgba(255, 255, 255, 0.1)' : theme.colors.background.default};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.colors.white : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  text-align: center;
  outline: none;
  
  ${({ $size, theme }) => {
    switch ($size) {
      case 'sm':
        return `
          width: 40px;
          height: 28px;
          font-size: ${theme.typography.fontSize.xs};
        `;
      case 'lg':
        return `
          width: 56px;
          height: 40px;
          font-size: ${theme.typography.fontSize.md};
        `;
      default:
        return `
          width: 48px;
          height: 32px;
          font-size: ${theme.typography.fontSize.sm};
        `;
    }
  }}
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  
  /* Remove number spinners */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  -moz-appearance: textfield;
`;

/**
 * Page size selector container
 */
const PageSizeContainer = styled.div<{ $size: PaginatorSize }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-left: ${({ theme }) => theme.spacing[4]};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ $size, theme }) => {
    switch ($size) {
      case 'sm':
        return theme.typography.fontSize.xs;
      case 'lg':
        return theme.typography.fontSize.md;
      default:
        return theme.typography.fontSize.sm;
    }
  }};
`;

/**
 * Page size select
 */
const PageSizeSelect = styled.select<{ $size: PaginatorSize; $inverse: boolean }>`
  border: 1px solid ${({ $inverse, theme }) =>
    $inverse ? 'rgba(255, 255, 255, 0.3)' : theme.colors.border.medium};
  background-color: ${({ $inverse, theme }) =>
    $inverse ? 'rgba(255, 255, 255, 0.1)' : theme.colors.background.default};
  color: ${({ $inverse, theme }) =>
    $inverse ? theme.colors.white : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  outline: none;
  
  ${({ $size, theme }) => {
    switch ($size) {
      case 'sm':
        return `
          height: 28px;
          padding: 4px 8px;
          font-size: ${theme.typography.fontSize.xs};
        `;
      case 'lg':
        return `
          height: 40px;
          padding: 8px 12px;
          font-size: ${theme.typography.fontSize.md};
        `;
      default:
        return `
          height: 32px;
          padding: 6px 10px;
          font-size: ${theme.typography.fontSize.sm};
        `;
    }
  }}
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// Arrow icons as SVG components
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const DoubleArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 17l-5-5 5-5" />
    <path d="M18 17l-5-5 5-5" />
  </svg>
);

const DoubleArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 17l5-5-5-5" />
    <path d="M6 17l5-5-5-5" />
  </svg>
);

/**
 * Calculate visible page numbers with ellipsis
 */
function getVisiblePages(
  currentPage: number,
  totalPages: number,
  maxVisible: number
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const sidePages = Math.floor((maxVisible - 3) / 2);

  // Always show first page
  pages.push(1);

  // Calculate start and end of middle section
  let startPage = Math.max(2, currentPage - sidePages);
  let endPage = Math.min(totalPages - 1, currentPage + sidePages);

  // Adjust if we're near the beginning
  if (currentPage <= sidePages + 2) {
    endPage = Math.min(totalPages - 1, maxVisible - 2);
    startPage = 2;
  }

  // Adjust if we're near the end
  if (currentPage >= totalPages - sidePages - 1) {
    startPage = Math.max(2, totalPages - maxVisible + 3);
    endPage = totalPages - 1;
  }

  // Add ellipsis before middle section if needed
  if (startPage > 2) {
    pages.push('ellipsis');
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis after middle section if needed
  if (endPage < totalPages - 1) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

/**
 * Paginator component - Navigation control for paginated content
 * Supports go-to-page input and page size selector
 *
 * @example
 * ```tsx
 * <Paginator
 *   currentPage={1}
 *   totalPages={10}
 *   onPageChange={(page) => setPage(page)}
 * />
 *
 * // Simple mode with go-to-page (ib-ui style)
 * <Paginator
 *   currentPage={5}
 *   totalPages={20}
 *   onPageChange={handlePageChange}
 *   simple
 *   showGoToPage
 * />
 *
 * // With page size selector
 * <Paginator
 *   currentPage={1}
 *   totalPages={10}
 *   onPageChange={handlePageChange}
 *   showPageSizeSelector
 *   pageSize={25}
 *   onPageSizeChange={handlePageSizeChange}
 * />
 * ```
 */
export const Paginator = forwardRef<HTMLDivElement, PaginatorProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      showFirstLast = false,
      showPrevNext = true,
      maxVisiblePages = 5,
      disabled = false,
      size = 'md',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      firstLabel,
      lastLabel,
      prevLabel,
      nextLabel,
      showGoToPage = false,
      goToPageDebounce = 300,
      showPageInfo,
      pageInfoFormat = (total) => `of ${total.toLocaleString()}`,
      loading = false,
      showPageSizeSelector = false,
      pageSize,
      onPageSizeChange,
      pageSizeOptions = defaultPageSizeOptions,
      simple = false,
      inverse = false,
      goToPageLabel = 'Go to page',
      pageSizeLabel = 'Items per page',
      ...rest
    },
    ref
  ) => {
    // Ensure valid page bounds
    const safeTotalPages = Math.max(1, totalPages);
    const safeCurrentPage = Math.min(Math.max(1, currentPage), safeTotalPages);

    const isFirstPage = safeCurrentPage === 1;
    const isLastPage = safeCurrentPage === safeTotalPages;
    const isDisabled = disabled || loading;

    // Internal state for go-to-page input
    const [goToPageValue, setGoToPageValue] = useState<string>(safeCurrentPage.toString());
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync go-to-page value with current page
    useEffect(() => {
      setGoToPageValue(safeCurrentPage.toString());
    }, [safeCurrentPage]);

    // Cleanup debounce on unmount
    useEffect(() => {
      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, []);

    const visiblePages = useMemo(
      () => getVisiblePages(safeCurrentPage, safeTotalPages, maxVisiblePages),
      [safeCurrentPage, safeTotalPages, maxVisiblePages]
    );

    const handlePageChange = useCallback(
      (page: number) => {
        if (page >= 1 && page <= safeTotalPages && page !== safeCurrentPage && !isDisabled) {
          onPageChange(page);
        }
      },
      [onPageChange, safeTotalPages, safeCurrentPage, isDisabled]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent, page: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handlePageChange(page);
        }
      },
      [handlePageChange]
    );

    // Handle go-to-page input change with debounce
    const handleGoToPageChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        
        // Only allow digits
        const numericValue = value.replace(/[^\d]/g, '');
        setGoToPageValue(numericValue);

        // Debounce the page change
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
          let page = parseInt(numericValue, 10);
          if (!isNaN(page)) {
            // Clamp to valid range
            page = Math.max(1, Math.min(page, safeTotalPages));
            setGoToPageValue(page.toString());
            if (page !== safeCurrentPage) {
              onPageChange(page);
            }
          } else if (numericValue === '') {
            setGoToPageValue(safeCurrentPage.toString());
          }
        }, goToPageDebounce);
      },
      [safeTotalPages, safeCurrentPage, onPageChange, goToPageDebounce]
    );

    // Handle go-to-page blur (normalize value)
    const handleGoToPageBlur = useCallback(() => {
      let page = parseInt(goToPageValue, 10);
      if (isNaN(page) || goToPageValue === '') {
        setGoToPageValue(safeCurrentPage.toString());
      } else {
        page = Math.max(1, Math.min(page, safeTotalPages));
        setGoToPageValue(page.toString());
      }
    }, [goToPageValue, safeCurrentPage, safeTotalPages]);

    // Handle page size change
    const handlePageSizeChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSize = parseInt(event.target.value, 10);
        onPageSizeChange?.(newSize);
      },
      [onPageSizeChange]
    );

    // Determine if we should show page info
    const shouldShowPageInfo = showPageInfo !== undefined ? showPageInfo : showGoToPage;

    // Don't render if only one page and not showing controls
    if (safeTotalPages <= 1 && !showPageSizeSelector) {
      return null;
    }

    // Simple mode - just prev/next with page input
    if (simple) {
      return (
        <PaginatorContainer
          ref={ref}
          className={className}
          $disabled={isDisabled}
          $inverse={inverse}
          role="navigation"
          aria-label="Pagination"
          data-testid={dataTestId || testId}
          data-id={dataId}
          {...rest}
        >
          <NavButton
            type="button"
            $size={size}
            $inverse={inverse}
            disabled={isDisabled || isFirstPage}
            onClick={() => handlePageChange(safeCurrentPage - 1)}
            aria-label={prevLabel || 'Previous page'}
          >
            <ArrowLeftIcon />
          </NavButton>

          <PageInfoContainer $size={size} $inverse={inverse}>
            {showGoToPage ? (
              <GoToPageInput
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                $size={size}
                $inverse={inverse}
                value={goToPageValue}
                onChange={handleGoToPageChange}
                onBlur={handleGoToPageBlur}
                disabled={isDisabled}
                aria-label={goToPageLabel}
                min={1}
                max={safeTotalPages}
              />
            ) : (
              <span>{safeCurrentPage}</span>
            )}
            {shouldShowPageInfo && (
              <span>{pageInfoFormat(safeTotalPages)}</span>
            )}
          </PageInfoContainer>

          <NavButton
            type="button"
            $size={size}
            $inverse={inverse}
            disabled={isDisabled || isLastPage}
            onClick={() => handlePageChange(safeCurrentPage + 1)}
            aria-label={nextLabel || 'Next page'}
          >
            <ArrowRightIcon />
          </NavButton>
        </PaginatorContainer>
      );
    }

    // Full mode with page numbers
    return (
      <PaginatorContainer
        ref={ref}
        className={className}
        $disabled={isDisabled}
        $inverse={inverse}
        role="navigation"
        aria-label="Pagination"
        data-testid={dataTestId || testId}
        data-id={dataId}
        {...rest}
      >
        {/* First Page Button */}
        {showFirstLast && (
          <NavButton
            type="button"
            $size={size}
            $inverse={inverse}
            disabled={isDisabled || isFirstPage}
            onClick={() => handlePageChange(1)}
            onKeyDown={(e) => handleKeyDown(e, 1)}
            aria-label={firstLabel || 'Go to first page'}
            aria-disabled={isFirstPage}
          >
            {firstLabel || <DoubleArrowLeftIcon />}
          </NavButton>
        )}

        {/* Previous Page Button */}
        {showPrevNext && (
          <NavButton
            type="button"
            $size={size}
            $inverse={inverse}
            disabled={isDisabled || isFirstPage}
            onClick={() => handlePageChange(safeCurrentPage - 1)}
            onKeyDown={(e) => handleKeyDown(e, safeCurrentPage - 1)}
            aria-label={prevLabel || 'Go to previous page'}
            aria-disabled={isFirstPage}
          >
            {prevLabel || <ArrowLeftIcon />}
          </NavButton>
        )}

        {/* Page Numbers */}
        {visiblePages.map((page, index) =>
          page === 'ellipsis' ? (
            <Ellipsis key={`ellipsis-${index}`} $size={size} aria-hidden="true">
              …
            </Ellipsis>
          ) : (
            <PageButton
              key={page}
              type="button"
              $size={size}
              $active={page === safeCurrentPage}
              $inverse={inverse}
              disabled={isDisabled}
              onClick={() => handlePageChange(page)}
              onKeyDown={(e) => handleKeyDown(e, page)}
              aria-label={`Page ${page}`}
              aria-current={page === safeCurrentPage ? 'page' : undefined}
            >
              {page}
            </PageButton>
          )
        )}

        {/* Next Page Button */}
        {showPrevNext && (
          <NavButton
            type="button"
            $size={size}
            $inverse={inverse}
            disabled={isDisabled || isLastPage}
            onClick={() => handlePageChange(safeCurrentPage + 1)}
            onKeyDown={(e) => handleKeyDown(e, safeCurrentPage + 1)}
            aria-label={nextLabel || 'Go to next page'}
            aria-disabled={isLastPage}
          >
            {nextLabel || <ArrowRightIcon />}
          </NavButton>
        )}

        {/* Last Page Button */}
        {showFirstLast && (
          <NavButton
            type="button"
            $size={size}
            $inverse={inverse}
            disabled={isDisabled || isLastPage}
            onClick={() => handlePageChange(safeTotalPages)}
            onKeyDown={(e) => handleKeyDown(e, safeTotalPages)}
            aria-label={lastLabel || 'Go to last page'}
            aria-disabled={isLastPage}
          >
            {lastLabel || <DoubleArrowRightIcon />}
          </NavButton>
        )}

        {/* Go to page input (in full mode) */}
        {showGoToPage && (
          <PageInfoContainer $size={size} $inverse={inverse}>
            <span>Go to</span>
            <GoToPageInput
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              $size={size}
              $inverse={inverse}
              value={goToPageValue}
              onChange={handleGoToPageChange}
              onBlur={handleGoToPageBlur}
              disabled={isDisabled}
              aria-label={goToPageLabel}
              min={1}
              max={safeTotalPages}
            />
            {shouldShowPageInfo && (
              <span>{pageInfoFormat(safeTotalPages)}</span>
            )}
          </PageInfoContainer>
        )}

        {/* Page Size Selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <PageSizeContainer $size={size}>
            <label htmlFor="page-size-select">{pageSizeLabel}:</label>
            <PageSizeSelect
              id="page-size-select"
              $size={size}
              $inverse={inverse}
              value={pageSize}
              onChange={handlePageSizeChange}
              disabled={isDisabled}
            >
              {pageSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </PageSizeSelect>
          </PageSizeContainer>
        )}
      </PaginatorContainer>
    );
  }
);

Paginator.displayName = 'Paginator';

export default Paginator;
